export interface Source {
  title: string;
  url: string;
  favicon?: string;
}

export type VisibleCategoryKey =
  | "company_info"
  | "adverse_news"
  | "risk_assessment"
  | "products_services"
  | "claims_history";

export type CategoryKey = VisibleCategoryKey | "leadership_linkedin";

export type CategoryStatus = "pending" | "in_progress" | "completed" | "error";

export interface CategoryState {
  status: CategoryStatus;
  data: Record<string, any> | null;
  sources: Source[];
  progressMessage: string;
}

export type CategoriesState = Record<CategoryKey, CategoryState>;

export const CATEGORY_LABELS: Record<VisibleCategoryKey, string> = {
  company_info: "Informações da Empresa",
  adverse_news: "Notícias Adversas",
  risk_assessment: "Avaliação de Risco",
  products_services: "Produtos e Serviços",
  claims_history: "Histórico de Sinistros",
};

export const CATEGORY_ORDER: VisibleCategoryKey[] = [
  "company_info",
  "adverse_news",
  "risk_assessment",
  "products_services",
  "claims_history",
];

export function blankCategoryState(): CategoryState {
  return { status: "pending", data: null, sources: [], progressMessage: "" };
}

export function initialCategoriesState(): CategoriesState {
  const state: Partial<CategoriesState> = {};
  for (const key of CATEGORY_ORDER) {
    state[key] = blankCategoryState();
  }
  state.leadership_linkedin = blankCategoryState();
  return state as CategoriesState;
}

export interface AuthUser {
  id: string;
  name: string | null;
  email: string;
  role: string;
  status: string;
}

export interface QuotaWindowSnapshot {
  used: number;
  limit: number | null;
  remaining: number | null;
  resets_at: string;
}

export interface QuotaSnapshot {
  daily: QuotaWindowSnapshot;
  monthly: QuotaWindowSnapshot;
  blocked_by: "daily" | "monthly" | null;
  exempt: boolean;
  time_zone: string;
}

export type RunStatus = "processing" | "completed" | "partial" | "failed";

export interface RunSourceOut {
  title: string;
  url: string;
  favicon: string | null;
}

export interface RunCategoryOut {
  category: string;
  status: "completed" | "failed";
  data: Record<string, unknown>;
  error_message: string | null;
  sources: RunSourceOut[];
}

export interface RunSummary {
  id: string;
  company_name: string;
  location: string | null;
  status: RunStatus;
  created_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  completed_category_count: number;
  source_count: number;
}

export interface RunDetail extends RunSummary {
  error_message: string | null;
  categories: RunCategoryOut[];
}

export interface RunListResponse {
  items: RunSummary[];
  next_cursor: string | null;
}

// Adaptador puro de reabertura (FR-024, FR-033, SC-003): reconstrói o mesmo
// formato que o ResultsDashboard já consome ao vivo a partir de uma execução
// persistida. O `data` e as `sources` voltam verbatim do jsonb, então nenhuma
// re-normalização acontece aqui — se um campo chegou como string JSON (ex.:
// `adverse_items`), ele permanece string.
export function categoriesStateFromRun(detail: RunDetail): CategoriesState {
  const state: Partial<CategoriesState> = {};
  const keys: CategoryKey[] = [...CATEGORY_ORDER, "leadership_linkedin"];

  for (const key of keys) {
    const row = detail.categories.find((category) => category.category === key);
    if (!row) {
      state[key] = blankCategoryState();
      continue;
    }
    state[key] = {
      status: row.status === "completed" ? "completed" : "error",
      data: row.data,
      sources: row.sources.map((source) => ({
        title: source.title,
        url: source.url,
        favicon: source.favicon ?? undefined,
      })),
      progressMessage: row.error_message ?? "",
    };
  }

  return state as CategoriesState;
}
